// 日本語での数字の表記を数値に変換するコード

const JapaneseDigitDefinitions = {
  ZERO: {kanjis: ["〇", "零"], value: 0},
  ONE: {kanjis: ["一", "壱", "壹"], value: 1},
  TWO: {kanjis: ["二", "弐", "貳"], value: 2},
  THREE: {kanjis: ["三", "参", "參"], value: 3},
  FOUR: {kanjis: ["四", "肆"], value: 4},
  FIVE: {kanjis: ["五", "伍"], value: 5},
  SIX: {kanjis: ["六", "陸"], value: 6},
  SEVEN: {kanjis: ["七", "漆"], value: 7},
  EIGHT: {kanjis: ["八", "捌"], value: 8},
  NINE: {kanjis: ["九", "玖"], value: 9},
  TEN: {kanjis: ["十", "拾"], value: 10},
  HUNDRED: {kanjis: ["百"], value: 100},
  THOUSAND: {kanjis: ["千"], value: 1000},
} as const;

const JapaneseDigit = Object.fromEntries(
  Object.entries(JapaneseDigitDefinitions).map(([key, {kanjis}]) => [
    key,
    kanjis,
  ])
) as {
  [K in keyof typeof JapaneseDigitDefinitions]: (typeof JapaneseDigitDefinitions)[K]["kanjis"];
};

const KANJI_DIGIT_MAP = Object.values(JapaneseDigitDefinitions).reduce(
  (map, {kanjis, value}) => {
    kanjis.forEach((kanji) => {
      map[kanji] = value;
    });
    return map;
  },
  {} as Record<string, number>
);

const JapaneseUnitDefinitions = {
  TRILLION: {kanji: "兆", value: 1_000_000_000_000},
  BILLION: {kanji: "億", value: 100_000_000},
  TEN_THOUSAND: {kanji: "万", value: 10_000},
  YEN: {kanji: "円", value: 1},
} as const;

const JapaneseUnit = Object.fromEntries(
  Object.entries(JapaneseUnitDefinitions).map(([key, {kanji}]) => [
    key,
    kanji,
  ])
) as {
  [K in keyof typeof JapaneseUnitDefinitions]: (typeof JapaneseUnitDefinitions)[K]["kanji"];
};

type JapaneseUnitType = (typeof JapaneseUnit)[keyof typeof JapaneseUnit];

const UNITS = Object.values(JapaneseUnitDefinitions).reduce(
  (map, {kanji, value}) => {
    map[kanji as JapaneseUnitType] = value;
    return map;
  },
  {} as Record<JapaneseUnitType, number>
);

const UNIT_ORDER: JapaneseUnitType[] = Object.values(JapaneseUnitDefinitions)
  .sort((a, b) => b.value - a.value)
  .map(({kanji}) => kanji as JapaneseUnitType);

type KanjiDigit = keyof typeof KANJI_DIGIT_MAP;
type PriceSegment = { value: number; unit: JapaneseUnitType };

// ヘルパー関数
const isFiniteAndSafe = (num: number): boolean =>
  Number.isFinite(num) && Math.abs(num) <= Number.MAX_SAFE_INTEGER;

const getAllKanjiDigits = (): string => {
  return Object.values(JapaneseDigit).flat().join("");
};

const getAllUnits = (): string => {
  return Object.values(JapaneseUnit).join("");
};

const isKanjiNumberString = (str: string): boolean => {
  const kanjiDigits = getAllKanjiDigits();
  const units = getAllUnits();
  const pattern = new RegExp(`^[${kanjiDigits}]+[${units}]?$`);
  
  if (!str.match(new RegExp(`[${units}]`))) return pattern.test(str);
  return str
    .split(new RegExp(`(?=[${units}])`))
    .every((part) => pattern.test(part));
};

const normalizeString = (input: string): string => {
  return input
    .replace(/[\n\r\t]/g, "")
    .replace(/[０-９]/g, (s) => String.fromCharCode(s.charCodeAt(0) - 0xfee0))
    .replace(/[．。]/g, ".")
    .replace(/，/g, ",")
    .replace(/[－＋]/g, "-")
    .replace(/\s+/g, "")
    .trim();
};

const hasInvalidCharacters = (str: string): boolean => {
  const validChars = `[^0-9\\.\\-,${getAllKanjiDigits()}${getAllUnits()}\\s]`;
  return new RegExp(validChars).test(str);
};

const validateBasicFormat = (str: string): boolean => {
  if (!str) return false;
  if (hasInvalidCharacters(str)) return false;
  if ((str.match(/\./g) || []).length > 1) return false;
  if (str.includes("-") || str.includes("+")) return false;
  if (/[.,]{2,}/.test(str)) return false;
  if (str.startsWith(JapaneseUnit.YEN)) return false;
  if (str.endsWith(".") || str.endsWith(",")) return false;
  if (!new RegExp(`[0-9${getAllKanjiDigits()}]`).test(str)) return false;
  if (
    new RegExp(
      `${JapaneseUnit.TRILLION}${JapaneseUnit.BILLION}|${JapaneseUnit.TRILLION}${JapaneseUnit.TEN_THOUSAND}|${JapaneseUnit.BILLION}${JapaneseUnit.TEN_THOUSAND}`
    ).test(str)
  )
    return false;
  if (/\.[^.]*[,.]/.test(str)) return false;
  if (str.endsWith(".円") || str.endsWith(",円")) return false;
  return true;
};

/**
 * 漢数字を数値に変換する
 */
const parseKanjiNumber = (kanjiString: string): number | null => {
  if (!kanjiString) return null;
  
  // 連続する一桁の漢数字の場合
  if (
    !new RegExp(
      `[${JapaneseDigit.TEN[0]}${JapaneseDigit.HUNDRED[0]}${JapaneseDigit.THOUSAND[0]}${JapaneseDigit.TEN[1]}]`
    ).test(kanjiString)
  ) {
    let result = 0;
    for (const char of kanjiString) {
      if (!(char in KANJI_DIGIT_MAP)) return null;
      const value = KANJI_DIGIT_MAP[char as KanjiDigit];
      if (value >= 10) return null;
      result = result * 10 + value;
    }
    return result;
  }
  
  // 位取りを含む漢数字の場合
  let total = 0;
  let currentNumber = 0;
  let lastUnit = 10000;
  
  for (let i = 0; i < kanjiString.length; i++) {
    const char = kanjiString[i];
    if (!(char in KANJI_DIGIT_MAP)) return null;
    
    const value = KANJI_DIGIT_MAP[char as KanjiDigit];
    if (value >= 10) {
      if (value >= lastUnit) return null;
      if (currentNumber === 0) currentNumber = 1;
      total += currentNumber * value;
      currentNumber = 0;
      lastUnit = value;
    } else {
      currentNumber = value;
    }
  }
  
  return total + (currentNumber > 0 ? currentNumber : 0);
};

/**
 * 漢数字の複合表現を処理する
 */
const parseKanjiComplex = (normalizedString: string): number | null => {
  if (!isKanjiNumberString(normalizedString)) return null;
  
  // 単位の順序チェック
  let lastUnitIndex = -1;
  let lastUnitPos = -1;
  for (const unit of [
    JapaneseUnit.TRILLION,
    JapaneseUnit.BILLION,
    JapaneseUnit.TEN_THOUSAND,
  ]) {
    const pos = normalizedString.indexOf(unit);
    if (pos !== -1) {
      const unitIndex = UNIT_ORDER.indexOf(unit as JapaneseUnitType);
      if (unitIndex <= lastUnitIndex && lastUnitIndex !== -1) return null;
      if (pos < lastUnitPos) return null;
      lastUnitIndex = unitIndex;
      lastUnitPos = pos;
    }
  }
  
  if (
    !new RegExp(
      `[${JapaneseUnit.TRILLION}${JapaneseUnit.BILLION}${JapaneseUnit.TEN_THOUSAND}]`
    ).test(normalizedString)
  ) {
    return parseKanjiNumber(normalizedString);
  }
  
  const unitValues: Record<string, number> = {};
  let currentString = normalizedString;
  
  for (const unit of [
    JapaneseUnit.TRILLION,
    JapaneseUnit.BILLION,
    JapaneseUnit.TEN_THOUSAND,
  ]) {
    if (currentString.includes(unit)) {
      const [prefix, ...rest] = currentString.split(unit);
      unitValues[unit] = prefix === "" ? 1 : parseKanjiNumber(prefix) ?? 0;
      currentString = rest.join(unit);
    }
  }
  
  if (currentString && currentString !== "") {
    const value = parseKanjiNumber(currentString);
    if (value === null) return null;
    unitValues[""] = value;
  }
  
  let total = 0;
  if (JapaneseUnit.TRILLION in unitValues)
    total += unitValues[JapaneseUnit.TRILLION] * UNITS[JapaneseUnit.TRILLION];
  if (JapaneseUnit.BILLION in unitValues)
    total += unitValues[JapaneseUnit.BILLION] * UNITS[JapaneseUnit.BILLION];
  if (JapaneseUnit.TEN_THOUSAND in unitValues)
    total +=
      unitValues[JapaneseUnit.TEN_THOUSAND] * UNITS[JapaneseUnit.TEN_THOUSAND];
  if ("" in unitValues) total += unitValues[""];
  
  return total;
};

/**
 * 複数の単位を含む複雑な表現を処理する
 */
const parseComplexWithMultipleUnits = (
  normalizedString: string
): number | null => {
  const yenIndex = normalizedString.indexOf(JapaneseUnit.YEN);
  if (yenIndex !== -1 && yenIndex < normalizedString.length - 1) return null;
  
  const unitPositions = UNIT_ORDER.map((unit) => ({
    unit,
    position: normalizedString.indexOf(unit),
  }))
    .filter(({position}) => position !== -1)
    .sort((a, b) => a.position - b.position);
  
  let lastUnitIndex = -1;
  for (const {unit} of unitPositions) {
    const unitIndex = UNIT_ORDER.indexOf(unit);
    if (
      unitIndex <= lastUnitIndex &&
      lastUnitIndex !== -1 &&
      unit !== JapaneseUnit.YEN
    )
      return null;
    if (
      unit === JapaneseUnit.YEN &&
      unitPositions.length > 1 &&
      unitPositions[unitPositions.length - 1].unit !== unit
    )
      return null;
    lastUnitIndex = unitIndex;
  }
  
  const unitValues: Record<JapaneseUnitType | string, number> = {
    [JapaneseUnit.TRILLION]: 0,
    [JapaneseUnit.BILLION]: 0,
    [JapaneseUnit.TEN_THOUSAND]: 0,
    [JapaneseUnit.YEN]: 0,
  };
  let lastPosition = 0;
  
  for (const {unit, position} of unitPositions) {
    const prefix = normalizedString.substring(lastPosition, position);
    if (prefix) {
      const value = new RegExp(`[${getAllKanjiDigits()}]`).test(prefix)
        ? parseKanjiNumber(prefix)
        : /^-?\d+\.?\d*$/.test(prefix)
          ? parseFloat(prefix)
          : null;
      if (value === null || isNaN(value)) return null;
      unitValues[unit] += value;
    } else {
      unitValues[unit] += 1;
    }
    lastPosition = position + 1;
  }
  
  if (lastPosition < normalizedString.length) {
    const suffix = normalizedString.substring(lastPosition);
    const value = new RegExp(`[${getAllKanjiDigits()}]`).test(suffix)
      ? parseKanjiNumber(suffix)
      : /^-?\d+\.?\d*$/.test(suffix)
        ? parseFloat(suffix)
        : null;
    if (value === null || isNaN(value)) return null;
    unitValues[JapaneseUnit.YEN] += value;
  }
  
  const total = Object.entries(UNITS).reduce(
    (sum, [unit, multiplier]) =>
      sum + unitValues[unit as JapaneseUnitType] * multiplier,
    0
  );
  
  return isFiniteAndSafe(total) ? total : null;
};

/**
 * 複合的な単位表現を処理する
 */
const parseComplexPrice = (normalizedString: string): number | null => {
  const yenIndex = normalizedString.indexOf(JapaneseUnit.YEN);
  if (yenIndex !== -1 && yenIndex < normalizedString.length - 1) return null;
  
  const unitPositions = UNIT_ORDER.map((unit) => ({
    unit,
    position: normalizedString.indexOf(unit),
  }))
    .filter(({position}) => position !== -1)
    .sort((a, b) => a.position - b.position);
  
  // 単位の順序と重複チェック
  let lastUnitIndex = -1;
  for (const {unit} of unitPositions) {
    const unitIndex = UNIT_ORDER.indexOf(unit);
    if (unitIndex <= lastUnitIndex && lastUnitIndex !== -1) return null;
    lastUnitIndex = unitIndex;
  }
  
  for (const unit of UNIT_ORDER) {
    if ((normalizedString.match(new RegExp(unit, "g")) || []).length > 1)
      return null;
  }
  
  // 単位の間に数値がないケースをチェック
  for (let i = 0; i < unitPositions.length - 1; i++) {
    if (unitPositions[i + 1].position - unitPositions[i].position === 1)
      return null;
  }
  
  const segments: PriceSegment[] = [];
  let remainingString = normalizedString;
  
  for (const {unit} of unitPositions) {
    const [prefix, ...rest] = remainingString.split(unit);
    let value: number;
    
    if (prefix === "") {
      value = 1;
    } else {
      value = new RegExp(`[${getAllKanjiDigits()}]`).test(prefix)
        ? parseKanjiNumber(prefix) ?? NaN
        : /^-?\d+\.?\d*$/.test(prefix)
          ? parseFloat(prefix)
          : NaN;
      if (isNaN(value)) return null;
    }
    
    segments.push({value, unit});
    remainingString = rest.join(unit);
  }
  
  if (remainingString && remainingString !== "") {
    if (
      segments.length > 0 &&
      segments[segments.length - 1].unit !== JapaneseUnit.YEN
    )
      return null;
    if (
      segments.length > 0 &&
      segments[segments.length - 1].unit === JapaneseUnit.YEN
    )
      return null;
    
    const value = new RegExp(`[${getAllKanjiDigits()}]`).test(remainingString)
      ? parseKanjiNumber(remainingString)
      : /^-?\d+\.?\d*$/.test(remainingString)
        ? parseFloat(remainingString)
        : null;
    if (value === null || isNaN(value)) return null;
    segments.push({value, unit: JapaneseUnit.YEN});
  }
  
  let total = 0;
  for (const {value, unit} of segments) {
    let adjustedValue = value;
    let unitValue = UNITS[unit];
    
    if (value < 1 && unit !== JapaneseUnit.YEN) {
      const decimalStr = value.toString().split(".")[1] || "";
      const decimalPlaces = decimalStr.length;
      
      if (unit === JapaneseUnit.TRILLION) {
        const scaleFactor = Math.pow(10, Math.min(decimalPlaces, 12));
        adjustedValue = Math.round(
          value *
          scaleFactor *
          (decimalPlaces > 12 ? Math.pow(10, decimalPlaces - 12) : 1)
        );
        unitValue =
          UNITS[JapaneseUnit.TRILLION] /
          scaleFactor /
          (decimalPlaces > 12 ? Math.pow(10, decimalPlaces - 12) : 1);
      } else {
        const scaleFactor = Math.pow(10, decimalPlaces);
        adjustedValue = Math.round(value * scaleFactor);
        unitValue = UNITS[unit] / scaleFactor;
      }
    }
    
    const segmentValue = adjustedValue * unitValue;
    if (!isFiniteAndSafe(segmentValue)) return null;
    total += segmentValue;
  }
  
  if (!isFiniteAndSafe(total)) return null;
  return total;
};

/**
 * 日本語の価格表示を数値に変換する
 */
export function parsePrice(
  priceString: string | null | undefined
): number | null {
  if (!priceString) return null;
  
  let normalizedString = normalizeString(priceString);
  if (!validateBasicFormat(normalizedString)) return null;
  
  if (normalizedString.endsWith(JapaneseUnit.YEN)) {
    normalizedString = normalizedString.slice(0, -1);
  }
  
  // 単位なしの数値の場合
  if (/^-?\d+\.?\d*$/.test(normalizedString)) {
    const num = parseFloat(normalizedString);
    return isFiniteAndSafe(num) ? num : null;
  }
  
  // 先頭がピリオドの場合
  if (normalizedString.startsWith(".")) {
    normalizedString = "0" + normalizedString;
  }
  
  normalizedString = normalizedString.replace(/,/g, "");
  
  // 漢数字のみの場合
  if (isKanjiNumberString(normalizedString)) {
    return parseKanjiNumber(normalizedString);
  }
  
  try {
    if (
      new RegExp(
        `^[${JapaneseUnit.TRILLION}${JapaneseUnit.BILLION}${JapaneseUnit.TEN_THOUSAND}]`
      ).test(normalizedString)
    )
      return null;
    if (isKanjiNumberString(normalizedString))
      return parseKanjiComplex(normalizedString);
    
    // 小数点を含む数値の場合は、そのまま返す
    if (/^\d+\.\d+$/.test(normalizedString)) {
      return parseFloat(normalizedString);
    }
    
    if (
      new RegExp(
        `${JapaneseUnit.TRILLION}.*${JapaneseUnit.BILLION}|${JapaneseUnit.BILLION}.*${JapaneseUnit.TEN_THOUSAND}|${JapaneseUnit.TRILLION}.*${JapaneseUnit.TEN_THOUSAND}`
      ).test(normalizedString)
    )
      return parseComplexWithMultipleUnits(normalizedString);
    return parseComplexPrice(normalizedString);
  } catch {
    return null;
  }
}
